import { useUser } from "@clerk/tanstack-react-start";
import { mapIdToCalendarDate, mapIdToVariant } from "@dailywars/core/engine";
import { toUtcDateString } from "@dailywars/core/entitlements/mapAccess";
import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";

import { PageEyeBrow } from "#/components/PageEyeBrow";
import { PageHeader } from "#/components/PageHeader";
import { PageIntro } from "#/components/PageIntro";
import { Badge } from "#/components/ui/badge";
import { Card, CardContent } from "#/components/ui/card";
import { Spinner } from "#/components/ui/spinner";
import { leaderboardMapDescription, leaderboardMapTitle, mapOgImageUrl } from "#/lib/documentTitle";
import { mapLeaderboardInfiniteQueryOptions } from "#/lib/queries";
import { mapIdRouteHead, mapIdRouteParams } from "#/lib/routeParams";
import { addUtcDays } from "#/lib/yearActivityGrid";

import {
  LeaderboardEmptyBody,
  LeaderboardFilledBody,
  LeaderboardMapHero,
} from "./-leaderboard.$mapId.ui";
import { LeaderboardStatusNote } from "./-leaderboard.statusNote";

export const Route = createFileRoute("/leaderboard_/$mapId")({
  params: mapIdRouteParams,
  loader: async ({ context, params: { mapId } }) => {
    const mapDate = mapIdToCalendarDate(mapId);
    const isLive = mapDate === toUtcDateString(new Date());
    await context.queryClient.ensureInfiniteQueryData(mapLeaderboardInfiniteQueryOptions(mapId));
    return { mapId, mapDate, isLive };
  },
  head: ({ params: { mapId } }) =>
    mapIdRouteHead({
      mapId,
      titleForMapId: leaderboardMapTitle,
      descriptionForMapId: leaderboardMapDescription,
      pathForMapId: (validMapId) => `/leaderboard/${validMapId}`,
      imageUrlForMapId: mapOgImageUrl,
    }),
  component: LeaderboardPage,
});

function leaderboardVariantLabel(mapId: string): "Standard" | "Premium" {
  return mapIdToVariant(mapId) === "premium" ? "Premium" : "Standard";
}

function leaderboardReplaysAvailable(mapDate: string, now = new Date()): boolean {
  const today = toUtcDateString(now);
  if (mapDate >= today) return false;
  return mapDate >= addUtcDays(today, -6);
}

function LeaderboardIntro({ mapDate, mapId }: { mapDate: string; mapId: string }) {
  const variantLabel = leaderboardVariantLabel(mapId);
  return (
    <PageIntro>
      <PageEyeBrow>Leaderboard</PageEyeBrow>
      <PageHeader>{mapDate}</PageHeader>
      <Badge variant="accent" className="px-4 py-3 text-sm">
        {variantLabel}
      </Badge>
    </PageIntro>
  );
}

function LeaderboardPage() {
  const { mapId, mapDate, isLive } = Route.useLoaderData();
  const { user } = useUser();
  const viewerPlayerId = user?.id;
  const variantLabel = leaderboardVariantLabel(mapId);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery(
    mapLeaderboardInfiniteQueryOptions(mapId),
  );

  const flatRows = useMemo(() => data?.pages.flatMap((p) => p.entries) ?? [], [data]);

  return (
    <main className="mx-auto w-full max-w-[1080px] px-4 py-10">
      <div className="mx-auto flex w-full max-w-lg flex-col items-center gap-6">
        <LeaderboardIntro mapDate={mapDate} mapId={mapId} />
        <LeaderboardMapHero mapId={mapId} />
        <LeaderboardStatusNote mapDate={mapDate} isLive={isLive} />
        <Card className="w-full overflow-hidden">
          {data === undefined ? (
            <CardContent className="flex items-center justify-center py-16">
              <Spinner />
            </CardContent>
          ) : flatRows.length === 0 ? (
            <LeaderboardEmptyBody />
          ) : (
            <LeaderboardFilledBody
              mapDate={mapDate}
              viewerPlayerId={viewerPlayerId}
              flatRows={flatRows}
              hasNextPage={hasNextPage ?? false}
              isFetchingNextPage={isFetchingNextPage}
              fetchNextPage={fetchNextPage}
              variantLabel={variantLabel}
              showReplayLinks={leaderboardReplaysAvailable(mapDate)}
            />
          )}
        </Card>
      </div>
    </main>
  );
}
